<template>
    <div
        :class="{
            'desktop-layout-form-view': $q.platform.is.desktop
        }"
    >
        <q-form @submit="isEdit ? update() : create()">
            <q-input
                v-model="form.name"
                @clear="form.name = ''"
                :rules="[val => val !== '' || $t('Enter a name')]"
                :label="$t('Name')"
                class="q-mb-xs"
                bg-color="white"
                clearable
                outlined
                lazy-rules
                color="accent"
            ></q-input>

            <div class="form__actions">
                <q-btn
                    v-if="isEdit"
                    @click="handleRemove"
                    :disable="$userCannot('edit', 'categories')"
                    :color="
                        $userCannot('edit', 'categories') ? 'grey' : 'negative'
                    "
                    class="float-left q-px-xs"
                    icon="fas fa-trash-alt"
                ></q-btn>
                <q-btn
                    :label="$t('Save')"
                    :disable="$userCannot('edit', 'categories')"
                    :color="
                        $userCannot('edit', 'categories') ? 'grey' : 'primary'
                    "
                    type="submit"
                    class="float-right"
                />
                <q-btn
                    @click="$router.go(-1)"
                    :label="$t('Cancel')"
                    class="float-right q-mr-sm"
                    text-color="primary"
                    color="white"
                    outline
                />
            </div>
        </q-form>
    </div>
</template>

<script>
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import { mapState, mapActions } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useFacilitiesStore } from '@stores/facilities';

export default {
    name: 'CategoryDetail',
    mixins: [BreadcrumbsManager],
    props: {
        categoryId: {
            type: [Number, String],
            required: false,
            default: null
        },
        facilityId: {
            type: [Number, String],
            required: true
        }
    },
    mounted() {
        // init form
        if (this.isEdit) {
            this.initForm();
        }

        // set breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.facility.name,
                route: {
                    name: 'facility_dashboard',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.$t('Settings'),
                route: {
                    name: 'facility_settings',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.$t('Downtime Causes'),
                route: {
                    name: 'cause_list',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.isEdit
                    ? this.$t('{category_name} (Category)', {
                          category_name: history.state.name
                      })
                    : this.$t('Create downtime category')
            }
        ]);
    },
    data() {
        return {
            baseURL: `facilities/${this.facilityId}/categories`,
            form: {
                name: ''
            }
        };
    },
    computed: {
        ...mapState(useFacilitiesStore, ['getFacility']),
        /**
         * Get selected facility.
         * @return {Object} - Facility.
         */
        facility() {
            return this.getFacility(this.facilityId);
        },
        /**
         * Determine if form is in edit mode.
         * @return {Boolean}
         */
        isEdit() {
            return this.categoryId !== null;
        },
        /**
         * Server formatted payload.
         * @return {Object}
         */
        payload() {
            return {
                facility: this.facilityId,
                name: this.form.name
            };
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        /**
         * Initialize form.
         */
        initForm() {
            this.form['name'] = history.state.name;
        },
        /**
         * Create cause.
         */
        async create() {
            // begin creating new cause
            this.beginLoading(`${this.$t('Creating new category')}...`);
            await this.$axios
                .post(`${this.baseURL}/`, this.payload)
                .then(response => {
                    this.$notifySuccess(this.$t('Category created'));
                    this.$router.go(-1);
                })
                .catch(error => {
                    this.handleApiErrors(error, 'create');
                })
                .finally(() => {
                    // stop loading
                    this.stopLoading();
                });
        },
        /**
         * Update cause.
         */
        async update() {
            // begin updating cause
            this.beginLoading(`${this.$t('Updating category')}...`);
            await this.$axios
                .put(`${this.baseURL}/${this.categoryId}/`, this.payload)
                .then(response => {
                    this.$notifySuccess(this.$t('Category updated'));
                    this.$router.go(-1);
                })
                .catch(error => {
                    this.handleApiErrors(error, 'update');
                })
                .finally(() => {
                    // stop loading
                    this.stopLoading();
                });
        },
        /**
         * Handle API errors based on the action being performed.
         * @param {Object} e - The error object returned from the API.
         * @param {String} action - The action that was being performed when the error occurred.
         */
        handleApiErrors(e, action) {
            const error =
                e?.response?.data && typeof e.response.data === 'object'
                    ? e.response.data
                    : {};
            if (
                'error_code' in error &&
                Array.isArray(error['error_code']) &&
                error['error_code'].includes('category_name_already_exists')
            ) {
                this.$notifyError(
                    this.$t(
                        'Another category with this name already exists. Please use a different name.'
                    ),
                    error
                );
            } else {
                if (action === 'create') {
                    this.$notifyError(
                        this.$t('Failed to create category'),
                        error
                    );
                } else {
                    this.$notifyError(
                        this.$t('Failed to update category'),
                        error
                    );
                }
            }
        },
        /**
         * Handle remove cause.
         */
        async handleRemove() {
            this.$confirmDelete(
                this.$t('Are you sure you want to delete this category?')
            ).onOk(async () => {
                await this.remove();
            });
        },
        /**
         * Remove cause.
         */
        async remove() {
            // begin updating cause
            this.beginLoading(`${this.$t('Deleting category')}...`);
            await this.$axios
                .delete(`${this.baseURL}/${this.categoryId}`)
                .then(response => {
                    this.$notifySuccess(this.$t('Category deleted'));
                    this.$router.go(-1);
                })
                .catch(error => {
                    if ('protected_error' in error) {
                        this.$notifyError(
                            this.$t(
                                'Unable to delete because the category is assigned to downtime cause(s). Unset category first to delete.'
                            )
                        );
                    } else {
                        this.$notifyError(
                            this.$t(
                                'Failed to remove category. Please try again later.'
                            ),
                            error
                        );
                    }
                })
                .finally(() => {
                    // stop loading
                    this.stopLoading();
                });
        }
    }
};
</script>
